import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";
import { formatPhone } from "@/lib/utils";

// Real footage of the actual home — a slow walk up the wheelchair ramp to the
// back deck. Served from /public so the browser streams it (no JS bundle hit).
const VIDEO_SRC = "/video/home.mp4";
const POSTER_SRC = "/video/home-poster.jpg";

export function WarmHero() {
  const { location, isSeattle } = useLocation();
  const basePath = isSeattle ? "/seattle" : "";
  const tel = location.primaryPhone.replace(/-/g, "");

  // Respect reduced-motion: a looping background video is vestibular motion, so
  // those users get the still poster. Initialise synchronously to avoid a
  // poster→video swap flash on the common (motion-OK) path.
  const [motionOk, setMotionOk] = useState(() =>
    typeof window !== "undefined"
      ? !window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setMotionOk(!mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  return (
    <section
      aria-label={`${location.name} — care that feels like home`}
      className="relative isolate flex min-h-[88vh] flex-col justify-end overflow-hidden"
    >
      {/* ── Background ──────────────────────────────────────────────────────── */}
      <div className="absolute inset-0 -z-10">
        {isSeattle ? (
          // Seattle has no real footage of its own — never borrow the Lynnwood
          // video. Use a deep brand panel (palette-aware) instead of fake imagery.
          // h-full/w-full (in-flow) mirrors the video layer so it paints reliably.
          <div className="h-full w-full band-deep grain" aria-hidden="true" />
        ) : (
          <>
            {motionOk ? (
              <video
                className="h-full w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={POSTER_SRC}
                aria-hidden="true"
              >
                <source src={VIDEO_SRC} type="video/mp4" />
              </video>
            ) : (
              <img
                src={POSTER_SRC}
                alt="The wheelchair-accessible ramp leading up to the back deck of the home"
                className="h-full w-full object-cover"
              />
            )}
            {/* Legibility scrim — deep navy from the bottom-left, clearing toward
                the top-right so the sunny sky stays visible. Brand hue, not black. */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, hsl(213 57% 12% / 0.92) 0%, hsl(213 57% 14% / 0.66) 34%, hsl(213 57% 16% / 0.18) 64%, hsl(213 57% 16% / 0) 100%)",
              }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to right, hsl(213 57% 12% / 0.55) 0%, hsl(213 57% 12% / 0) 58%)",
              }}
              aria-hidden="true"
            />
          </>
        )}
      </div>

      {/* ── Content — anchored bottom-left, intentionally off-center ─────────── */}
      <div className="container relative mx-auto px-6 pb-16 pt-28 md:pb-24">
        <div className="hero-stagger max-w-2xl">
          {/* No brand lockup here — the header is the single logo/name anchor on
              this screen. The hero leads straight into the promise. */}
          <div
            style={{ "--i": 0 } as React.CSSProperties}
            className="flex items-center gap-3 text-white/75"
          >
            <span className="eyebrow">{location.cityState.replace(/,.*/, "")}, WA</span>
            <span className="h-3 w-px bg-white/30" aria-hidden="true" />
            <span className="eyebrow inline-flex items-center gap-1.5 text-[hsl(var(--accent-on-dark))]">
              <ShieldCheck className="h-3.5 w-3.5" />
              DSHS Licensed
            </span>
          </div>

          <h1
            style={{ "--i": 2 } as React.CSSProperties}
            className="mt-5 display-xl font-heading font-semibold tracking-[-0.03em] text-white"
          >
            Care that feels{" "}
            <span className="font-display-italic font-normal text-[hsl(var(--accent-on-dark))]">like home.</span>
          </h1>

          <p
            style={{ "--i": 3 } as React.CSSProperties}
            className="mt-5 max-w-lg text-lg leading-relaxed text-white/85"
          >
            A licensed adult family home in {location.shortName} — warm, around-the-clock
            care where your parent is known by name, not a room number.
          </p>

          {/* Actions — click-to-call is the highest-ROI element, so it's a peer of
              the tour CTA, not a footnote. */}
          <div
            style={{ "--i": 4 } as React.CSSProperties}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4"
          >
            <a
              href={`tel:${tel}`}
              className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-accent px-7 py-4 text-base font-semibold text-accent-foreground shadow-lg transition-[transform,box-shadow] duration-200 ease-out-expo hover:shadow-xl active:scale-[0.98]"
            >
              <Phone className="h-[1.125rem] w-[1.125rem]" />
              Call {formatPhone(location.primaryPhone)}
            </a>
            <Link
              to={`${basePath}/contact#tour-form`}
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/35 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/12"
            >
              {isSeattle ? "Join the waitlist" : "Schedule a private tour"}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:translate-x-0.5" />
            </Link>
          </div>

          {/* Trust strip — only verifiable claims (no fabricated bonded/insured). */}
          <div
            style={{ "--i": 5 } as React.CSSProperties}
            className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/70"
          >
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--accent-on-dark))]" />
              {isSeattle ? "WA DSHS Licensed" : `WA License #${location.licenseNumber}`}
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-white/40 sm:block" aria-hidden="true" />
            <span>5 years caring for families</span>
            <span className="hidden h-1 w-1 rounded-full bg-white/40 sm:block" aria-hidden="true" />
            <span>A direct line to your caregiver</span>
          </div>
        </div>
      </div>
    </section>
  );
}
